import { useState, useRef, useCallback } from "react";

interface UseRecorderReturn {
  isRecording: boolean;
  duration: number;
  start: () => Promise<void>;
  stop: () => Promise<Blob>;
  error: string | null;
}

export function useRecorder(): UseRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<ReturnType<typeof setInterval>>();

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunks.current = [];
      recorder.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
      recorder.start(1000);
      mediaRecorder.current = recorder;
      setIsRecording(true);
      setDuration(0);
      timer.current = setInterval(() => setDuration((d) => d + 1), 1000);
    } catch (e: any) {
      setError(e.message ?? "Microphone access denied");
    }
  }, []);

  const stop = useCallback((): Promise<Blob> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorder.current;
      if (!recorder) return resolve(new Blob());
      recorder.onstop = () => {
        clearInterval(timer.current);
        setIsRecording(false);
        recorder.stream.getTracks().forEach((t) => t.stop());
        resolve(new Blob(chunks.current, { type: "audio/webm" }));
      };
      recorder.stop();
    });
  }, []);

  return { isRecording, duration, start, stop, error };
}
